import React, { useEffect, useMemo, useRef, useState } from 'react';

import styled from '@emotion/styled';

import { FontWeight } from '@clutter/clean';

import { RateGroupKindEnum } from '@graphql/platform';

import { useSharedCheckoutContext } from '@root/components/checkout/context';
import {
  COMMITMENT_TO_FREE_SUBSEQUENT_JOBS,
  DIMENSIONAL_PLANS,
  PLANS,
} from '@root/constants/pricing';
import { useTrack } from '@root/initializers/wt';
import { Commitment as CommitmentType } from '@root/resources/types/commitment';
import { PlanName } from '@root/resources/types/plan_name';
import { EventSchema } from '@root/resources/wt/types';

import { formatCurrency } from '@utils/formatters';
import { useStoragePriceCompletedFunnelEvent } from '@utils/hooks/funnel_events/use_storage_price_completed_funnel_event';
import { useTrackFunnelEvents } from '@utils/hooks/funnel_events/use_track_funnel_event';
import { useBreakpoints } from '@utils/hooks/use_breakpoints';
import { useLastDefined } from '@utils/hooks/use_last_defined';
import { extractStoragePrice } from '@utils/pricing/pricing_set';

import recommendCheck from '@images/checkout/challenger/recommend_check.svg';
import unit5x5 from '@images/checkout/challenger/unit_5x5.png';
import unit5x10 from '@images/checkout/challenger/unit_5x10.png';
import unit10x10 from '@images/checkout/challenger/unit_10x10.png';
import unit10x15 from '@images/checkout/challenger/unit_10x15.png';
import unit10x20 from '@images/checkout/challenger/unit_10x20.png';
import unit10x25 from '@images/checkout/challenger/unit_10x25.png';
import unit10x30 from '@images/checkout/challenger/unit_10x30.png';
import unit10x40 from '@images/checkout/challenger/unit_10x40.png';
import unit10x50 from '@images/checkout/challenger/unit_10x50.png';

import { FinalDeliveryCostModal } from '../final_delivery_cost_modal';
import { commitmentDiscountPercents } from '../hooks/commitment_discounts';
import {
  Banner,
  BannerTitle,
  CardPadded,
  FeatureCheck,
  HelpText,
  InlineBrandLink,
  Pill,
  Question,
} from '../shared';
import {
  selectedItemsFromStorageCalculatorItems,
  storageCalculatorItemsFromSelectedItems,
} from '../storage_calculator_items';
import { StorageCalculatorModal } from '../storage_calculator_modal';
import { CHALLENGER as T } from '../tokens';

/** Figma unit-size illustrations (314×240 frame). */
const PLAN_ILLUSTRATION: Record<PlanName, string> = {
  '5x5': unit5x5,
  '5x10': unit5x10,
  '10x10': unit10x10,
  '10x15': unit10x15,
  '10x20': unit10x20,
  '10x25': unit10x25,
  '10x30': unit10x30,
  '10x40': unit10x40,
  '10x50': unit10x50,
};

const DESKTOP = '@media (min-width: 840px)';
const MOBILE = '@media (max-width: 839px)';

/** Figma 707:20077 — mobile shows 3 endpoint labels; slider still steps all plans. */
const MOBILE_SLIDER_LABEL_NAMES: PlanName[] = ['5x5', '10x25', '10x50'];
/** Figma 1052:17116 mobile / 707:10099 desktop — 8 visual track segments. */
const SLIDER_SEGMENT_COUNT = 8;
/** Figma 707:10088 / 1427:19567 — thumb pill on the track. */
const SLIDER_THUMB_WIDTH_PX = 32;

/**
 * Fill + thumb position on the 8-segment track.
 * Plan 0 (5×5) inset per Figma 1427:19541; plans 1–7 on tick dividers; last at 100%.
 */
function sliderPositionPct(planIndex: number, planCount: number): number {
  if (planCount <= 0) return 0;
  if (planIndex >= planCount - 1) return 100;
  if (planIndex === 0) return (0.5 / planCount) * 100;
  return (planIndex / SLIDER_SEGMENT_COUNT) * 100;
}

/** Figma 27:2989 — feature rows under What’s included. */
const InclusionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${T.space[8]};
  width: 100%;
`;

const COMMITMENTS: Array<{
  value: CommitmentType;
  label: string;
}> = [
  { value: RateGroupKindEnum.Mover, label: 'Month to month' },
  { value: RateGroupKindEnum.Flexer, label: '4-month minimum' },
  { value: RateGroupKindEnum.Saver, label: '8-month minimum' },
];

const HeaderBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${T.space[8]};
  width: 100%;
`;

/** Figma 144:2648 / 29:3382 — Body/Medium 400, wrap like canvas text. */
const DurationHelpText = styled(HelpText)`
  word-break: break-word;

  ${MOBILE} {
    font-size: 14px;
    line-height: 20px;
  }
`;

/**
 * Inline span (not a button box) so wrap can fall after “storage”, matching
 * Figma 144:2648 mobile and 29:3382 desktop — “calculator to help…” stays
 * together on the next line.
 */
const DurationBrandLink = styled.span`
  color: ${T.color.linkBrand};
  cursor: pointer;
  font-family: ${T.font.sans};
  font-size: 14px;
  font-style: normal;
  font-weight: ${FontWeight.Semibold};
  line-height: 19px;
  word-break: break-word;

  ${MOBILE} {
    line-height: 20px;
  }

  &:hover {
    opacity: 0.85;
  }
`;

/**
 * Figma Banner-small 936:16447 — recommendation after storage calculator.
 * summarySurface, pad 12, gap 8, icon-with-bg + Demi Bold title / Regular body.
 */
const RecommendBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${T.space[8]};
  width: 100%;
  padding: ${T.space[12]};
  box-sizing: border-box;
  background: ${T.color.summarySurface};
  border-radius: ${T.radius.md};
`;

/** Figma icon-with-bg: 28×28 white shell + brand-25 fill, 20×20 glyph slot. */
const RecommendBadge = styled.span`
  position: relative;
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 4px;
  box-sizing: border-box;
  border-radius: ${T.radius.sm};
  background: ${T.color.bgPrimary};

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: ${T.radius.sm};
    background: rgba(8, 170, 168, 0.25);
  }
`;

const RecommendIconBox = styled.span`
  position: relative;
  z-index: 1;
  display: block;
  width: 20px;
  height: 20px;
  overflow: hidden;
  flex-shrink: 0;
`;

/** Figma icon/done leaf inset 22.08% / 13.33% inside 20×20. */
const RecommendIcon = styled.img`
  position: absolute;
  top: 4.42px;
  left: 2.67px;
  width: 14.67px;
  height: 11.17px;
  display: block;
`;

const RecommendCopy = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  font-family: ${T.font.sans};
  color: ${T.color.textPrimary};
`;

const RecommendTitle = styled.div`
  font-size: 14px;
  font-weight: ${FontWeight.Semibold};
  line-height: 19px;
`;

const RecommendBody = styled.div`
  font-size: 14px;
  font-weight: ${FontWeight.Regular};
  line-height: 20px;
`;

const CommitmentRow = styled.div`
  display: flex;
  width: 100%;

  ${MOBILE} {
    flex-direction: column;
    gap: 4px;
  }

  ${DESKTOP} {
    flex-direction: row;
    gap: ${T.space[16]};
  }
`;

const CommitmentOption = styled.button<{ selected?: boolean }>`
  appearance: none;
  min-width: 0;
  cursor: pointer;
  background: ${({ selected }) =>
    selected ? T.color.bgSelected : T.color.bgPrimary};
  border: 1px solid
    ${({ selected }) =>
      selected ? T.color.borderBrand : T.color.borderDefault};
  border-radius: ${T.radius.md};
  padding: ${T.space[12]} 0;
  font-family: ${T.font.sans};

  ${MOBILE} {
    flex: none;
    width: 100%;
  }

  ${DESKTOP} {
    flex: 1 0 0;
  }
`;

const CommitmentInner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${T.space[4]};

  ${MOBILE} {
    padding: 0 ${T.space[12]};
  }

  ${DESKTOP} {
    padding: 0 ${T.space[16]};
  }
`;

const CommitmentLabel = styled.span`
  font-weight: ${FontWeight.Medium};
  color: ${T.color.textPrimary};

  ${MOBILE} {
    font-size: 13px;
    line-height: 18px;
  }

  ${DESKTOP} {
    font-size: 14px;
    line-height: 21px;
  }
`;

const IllustrationWrap = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${T.space[16]} 0;
  box-sizing: border-box;

  ${MOBILE} {
    height: 224px;
  }

  ${DESKTOP} {
    height: 280px;
  }
`;

/** Desktop Figma 314×240; mobile scales inside 224px wrap. */
const IllustrationFrame = styled.div`
  position: relative;
  flex-shrink: 0;

  ${MOBILE} {
    width: 100%;
    max-width: 280px;
    height: 192px;
  }

  ${DESKTOP} {
    width: 314px;
    height: 240px;
  }
`;

const IllustrationImg = styled.img`
  display: block;
  object-fit: contain;

  ${MOBILE} {
    width: 100%;
    height: 192px;
  }

  ${DESKTOP} {
    width: 314px;
    height: 240px;
  }
`;

const PriceBlock = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${T.space[4]};
  padding: ${T.space[12]} 0;
`;

const Price = styled.div`
  font-weight: ${FontWeight.Medium};
  color: ${T.color.textPrimary};

  ${MOBILE} {
    font-size: 16px;
    line-height: 22px;
  }

  ${DESKTOP} {
    font-size: 20px;
    line-height: 24px;
  }
`;

const PlanCaption = styled.div`
  font-weight: ${FontWeight.Regular};
  color: ${T.color.textPrimary};

  ${MOBILE} {
    font-size: 13px;
    line-height: 18px;
  }

  ${DESKTOP} {
    font-size: 14px;
    line-height: 20px;
  }
`;

const SliderWrap = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: ${T.space[48]};

  ${DESKTOP} {
    /* Room for teal size helper above the thumb (Funnel-Feedback 29:3429). */
    padding-top: 20px;
  }
`;

const SliderTrack = styled.div`
  position: relative;
  width: 100%;
  height: 8px;
  border-radius: ${T.radius.xs};
  background: ${T.color.bgButtonDisabled};
  overflow: visible;
`;

/**
 * Figma Funnel-Feedback — 2px dark dividers across 8 segment boundaries.
 * Hide only the leading edge divider; 7/8 aligns with 10′ × 40′.
 */
const SliderTickMarks = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  display: none;
  pointer-events: none;
  border-radius: ${T.radius.xs};
  overflow: hidden;

  ${MOBILE} {
    display: flex;
  }

  ${DESKTOP} {
    display: flex;
  }
`;

const SliderTickMark = styled.div<{ $hidden?: boolean }>`
  flex: 1 0 0;
  display: flex;
  align-items: center;
  min-width: 0;

  &::before {
    content: '';
    width: 2px;
    height: 8px;
    flex-shrink: 0;
    background: ${({ $hidden }) =>
      $hidden ? 'transparent' : T.color.textPrimary};
  }
`;

/**
 * Figma 1427:19552 — teal fill to thumb center (plan 0 ≈ 38px on 672px track).
 */
const SliderFill = styled.div<{ pct: number }>`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  z-index: 0;
  width: ${({ pct }) => pct}%;
  box-sizing: border-box;
  background: ${T.color.iconBrand};
  border-radius: ${T.radius.xs};
`;

/**
 * Figma 1427:19567 — thumb centered on plan slot (plan 0 left ≈ 21px on 672px).
 */
const SliderThumb = styled.div<{
  pct: number;
  $atEnd?: boolean;
}>`
  position: absolute;
  top: 50%;
  left: ${({ pct }) => pct}%;
  z-index: 2;
  transform: ${({ $atEnd }) =>
    $atEnd ? 'translate(-100%, -50%)' : 'translate(-50%, -50%)'};
  width: ${SLIDER_THUMB_WIDTH_PX}px;
  height: 16px;
  border-radius: ${T.radius.pill};
  background: ${T.color.bgPrimary};
  border: 1px solid ${T.color.bgPrimary};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.18);
  pointer-events: none;
`;

/** Full-track range; discrete steps map to tick centers via visual pct. */
const SliderInput = styled.input`
  position: absolute;
  inset: 0;
  z-index: 3;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
  margin: 0;
`;

/**
 * Aligns a label/bubble to the thumb center. Last plan thumb is flush-right
 * (32px wide), so center sits 16px left of the track end.
 */
const thumbCenterStyle = (pct: number, atEnd?: boolean) => `
  left: ${atEnd ? '100%' : `${pct}%`};
  transform: ${
    atEnd ? 'translate(calc(-16px - 50%), 0)' : 'translate(-50%, 0)'
  };
`;

/** Funnel-Feedback 29:3429 — desktop teal size helper above the thumb. */
const DesktopSizeHint = styled.div<{ pct: number; $atEnd?: boolean }>`
  display: none;

  ${DESKTOP} {
    display: block;
    position: absolute;
    top: 0;
    ${({ pct, $atEnd }) => thumbCenterStyle(pct, $atEnd)};
    z-index: 2;
    font-family: ${T.font.sans};
    font-size: 12px;
    font-weight: ${FontWeight.Medium};
    line-height: 16px;
    color: ${T.color.textBrand};
    text-align: center;
    white-space: nowrap;
    pointer-events: none;
  }
`;

/** Funnel-Feedback 27:2212 — mobile teal bubble while engaging the slider. */
const MobileSizeBubble = styled.div<{
  pct: number;
  $atEnd?: boolean;
  $visible: boolean;
}>`
  display: none;

  ${MOBILE} {
    display: ${({ $visible }) => ($visible ? 'flex' : 'none')};
    position: absolute;
    top: calc(8px + 8px);
    ${({ pct, $atEnd }) => thumbCenterStyle(pct, $atEnd)};
    z-index: 6;
    flex-direction: column;
    align-items: center;
    pointer-events: none;
  }
`;

/** Funnel-Feedback 27:2225 — 78×46 pointer, overlaps the pill by ~13px. */
const MobileBubbleCaret = styled.div`
  width: 78px;
  height: 46px;
  margin-bottom: -13px;
  background: rgba(8, 170, 168, 0.8);
  clip-path: polygon(50% 0, 100% 100%, 0 100%);
  filter: drop-shadow(0 1px 1px rgba(0, 0, 0, 0.08));
`;

/** Funnel-Feedback 27:2212 — 206×95 pill, Body Medium ~37/43. */
const MobileBubbleBody = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 206px;
  height: 95px;
  padding: 26px 35px;
  border-radius: ${T.radius.pill};
  background: rgba(8, 170, 168, 0.8);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  box-shadow: 0 9px 9px rgba(0, 0, 0, 0.15);
  font-family: ${T.font.sans};
  font-size: 37px;
  font-weight: ${FontWeight.Medium};
  line-height: 43px;
  color: ${T.color.textInverse};
  white-space: nowrap;
`;

const TickLabels = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
  width: 100%;
  font-size: 12px;
  font-weight: ${FontWeight.Medium};
  line-height: 16px;
  color: ${T.color.textTertiary};
  text-align: center;

  ${MOBILE} {
    font-size: 10px;
    line-height: 14px;
  }
`;

const Tick = styled.span<{
  hide?: boolean;
  $align?: 'left' | 'center' | 'right';
}>`
  flex: 1 0 0;
  opacity: ${({ hide }) => (hide ? 0 : 1)};
  text-align: ${({ $align }) => $align ?? 'center'};
`;

/** Desktop 24; mobile Storage options (1052:19914) uses 12 between sections. */
const Stack = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  ${MOBILE} {
    gap: ${T.space[12]};
  }

  ${DESKTOP} {
    gap: ${T.space[24]};
  }
`;

type Props = {
  planSize?: PlanName;
  commitment?: CommitmentType;
  onPlanSizeChange: (plan: PlanName) => void;
  onCommitmentChange: (commitment: CommitmentType) => void;
};

export const PlanAndCommitmentSection: React.FC<Props> = ({
  planSize,
  commitment,
  onPlanSizeChange,
  onCommitmentChange,
}) => {
  const [showEstimator, setShowEstimator] = useState(false);
  const [showFinalDeliveryCost, setShowFinalDeliveryCost] = useState(false);
  const [recommendedPlan, setRecommendedPlan] = useState<PlanName | null>(null);
  /** Mobile size bubble while the user is dragging/focusing the slider. */
  const [sliderEngaged, setSliderEngaged] = useState(false);
  const track = useTrack({ action: 'click' });
  const trackFunnelEvent = useTrackFunnelEvents();
  const trackStoragePriceCompleted = useStoragePriceCompletedFunnelEvent();
  const pricedOnceRef = useRef(false);

  const openStorageCalculator = () => {
    setShowEstimator(true);
    track({
      objectName: 'plan_size_selector',
      label: 'Storage calculator',
      objectType: 'button',
    });
  };
  const {
    pricingSummary: summary,
    defaultPricingSummary,
    pricingSet,
    flowState: { values, onChange: onFlowChange },
  } = useSharedCheckoutContext();
  const pricingSummary = useLastDefined(summary) ?? defaultPricingSummary;
  const storageCalculatorItems = useMemo(
    () =>
      selectedItemsFromStorageCalculatorItems(values.storageCalculatorItems),
    [values.storageCalculatorItems],
  );
  const storagePriceViewedRef = useRef(false);

  useEffect(() => {
    if (!pricingSet || storagePriceViewedRef.current) return;

    storagePriceViewedRef.current = true;
    trackFunnelEvent({
      schema: EventSchema.WWW__StoragePriceViewed,
      action: 'display',
      metadata: {},
    });
  }, [pricingSet]);

  const { isDesktop } = useBreakpoints();
  const useMobileLabels = !isDesktop;

  const planCount = DIMENSIONAL_PLANS.length;
  const planIndex = Math.max(
    0,
    DIMENSIONAL_PLANS.findIndex((p) => p.name === planSize),
  );
  const selectedPlan = DIMENSIONAL_PLANS[planIndex] ?? DIMENSIONAL_PLANS[0];
  const isLastPlan = planCount > 0 && planIndex >= planCount - 1;
  const fillPct = sliderPositionPct(planIndex, planCount);
  const thumbPct = fillPct;

  const monthlyAmount = useMemo(() => {
    if (!commitment || !selectedPlan || !pricingSet) {
      return pricingSummary?.monthlyAmount;
    }
    return (
      extractStoragePrice(commitment, selectedPlan.name, pricingSet) ??
      pricingSummary?.monthlyAmount
    );
  }, [commitment, selectedPlan, pricingSet, pricingSummary]);

  /**
   * Commitment pill % off month-to-month — from ZIP pricing set + selected size
   * (updates when ZIP or slider/calculator size changes; not hardcoded).
   */
  const discountByCommitment = useMemo(
    () => commitmentDiscountPercents(selectedPlan.name, pricingSet),
    [selectedPlan.name, pricingSet],
  );

  // Classic fires storage_price_completed on plan + commitment select. Challenger
  // pre-selects defaults, so also fire once when price first resolves so funnel
  // drop-off can be compared without requiring an extra slider drag.
  useEffect(() => {
    if (
      pricedOnceRef.current ||
      !planSize ||
      !commitment ||
      monthlyAmount == null
    ) {
      return;
    }
    pricedOnceRef.current = true;
    trackStoragePriceCompleted({
      planSize,
      commitment,
      amount: monthlyAmount,
    });
  }, [planSize, commitment, monthlyAmount, trackStoragePriceCompleted]);

  const trackPriceCompleted = (
    nextPlan: PlanName,
    nextCommitment: CommitmentType,
  ) => {
    if (!pricingSet) return;
    const amount = extractStoragePrice(nextCommitment, nextPlan, pricingSet);
    if (amount == null) return;
    pricedOnceRef.current = true;
    trackStoragePriceCompleted({
      planSize: nextPlan,
      commitment: nextCommitment,
      amount,
    });
  };

  const freeJobs =
    commitment != null ? COMMITMENT_TO_FREE_SUBSEQUENT_JOBS[commitment] : 0;
  const commitmentMonths =
    commitment === RateGroupKindEnum.Saver
      ? 8
      : commitment === RateGroupKindEnum.Flexer
        ? 4
        : null;
  // Labor hours from ZIP/commitment quote; Figma sample uses “2”.
  const freeLaborHours =
    pricingSummary?.freeDuration != null
      ? Math.round(pricingSummary.freeDuration)
      : 2;
  const inclusionTitle =
    commitment === RateGroupKindEnum.Mover
      ? 'What’s included with your monthly plan'
      : commitmentMonths != null
        ? `What’s included with your ${commitmentMonths}-month plan`
        : undefined;

  return (
    <CardPadded>
      <Stack>
        <HeaderBlock>
          <Question>How long and how much storage do you need?</Question>
          <DurationHelpText>
            If your items require more or less space than you reserve, your rate
            will be adjusted. Use our{' '}
            <DurationBrandLink
              role="button"
              tabIndex={0}
              onClick={openStorageCalculator}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  openStorageCalculator();
                }
              }}
            >
              storage
              <br />
              calculator
            </DurationBrandLink>{' '}
            to help you estimate how much space to reserve.
          </DurationHelpText>
        </HeaderBlock>

        <CommitmentRow>
          {COMMITMENTS.map((option) => {
            const selected = commitment === option.value;
            const discount = discountByCommitment[option.value];
            return (
              <CommitmentOption
                key={option.value}
                type="button"
                selected={selected}
                onClick={() => {
                  if (planSize) {
                    trackPriceCompleted(planSize, option.value);
                  }
                  onCommitmentChange(option.value);
                }}
              >
                <CommitmentInner>
                  <CommitmentLabel>{option.label}</CommitmentLabel>
                  {discount != null && <Pill>-{discount}%</Pill>}
                </CommitmentInner>
              </CommitmentOption>
            );
          })}
        </CommitmentRow>

        {recommendedPlan && (
          <RecommendBanner>
            <RecommendBadge>
              <RecommendIconBox>
                <RecommendIcon src={recommendCheck} alt="" />
              </RecommendIconBox>
            </RecommendBadge>
            <RecommendCopy>
              <RecommendTitle>
                We recommend a {PLANS[recommendedPlan].label} unit
              </RecommendTitle>
              <RecommendBody>
                You can adjust your plan size when we arrive for pickup.
              </RecommendBody>
            </RecommendCopy>
          </RecommendBanner>
        )}

        <div>
          <IllustrationWrap>
            <IllustrationFrame>
              <IllustrationImg
                src={PLAN_ILLUSTRATION[selectedPlan.name]}
                alt={`${selectedPlan.label} storage size`}
                width={314}
                height={240}
              />
            </IllustrationFrame>
          </IllustrationWrap>
          <PriceBlock>
            <Price>
              {monthlyAmount != null
                ? `${formatCurrency(monthlyAmount, {
                    maximumFractionDigits: 0,
                  })}/mo`
                : '—/mo'}
            </Price>
            <PlanCaption>
              {selectedPlan.label} - {selectedPlan.description}
            </PlanCaption>
          </PriceBlock>
        </div>

        <SliderWrap>
          <DesktopSizeHint pct={thumbPct} $atEnd={isLastPlan}>
            {selectedPlan.label}
          </DesktopSizeHint>
          <SliderTrack>
            <SliderFill pct={fillPct} />
            <SliderTickMarks aria-hidden>
              {Array.from({ length: SLIDER_SEGMENT_COUNT }).map((_, i) => (
                <SliderTickMark key={i} $hidden={i === 0} />
              ))}
            </SliderTickMarks>
            <SliderThumb pct={thumbPct} $atEnd={isLastPlan} />
            <SliderInput
              type="range"
              min={0}
              max={planCount - 1}
              step={1}
              value={planIndex}
              aria-label="Storage plan size"
              aria-valuetext={selectedPlan.label}
              onPointerDown={(e) => {
                e.currentTarget.setPointerCapture(e.pointerId);
                setSliderEngaged(true);
              }}
              onPointerUp={() => setSliderEngaged(false)}
              onPointerCancel={() => setSliderEngaged(false)}
              onBlur={() => setSliderEngaged(false)}
              onChange={(e) => {
                const next = DIMENSIONAL_PLANS[Number(e.target.value)];
                if (!next) return;
                if (commitment) {
                  trackPriceCompleted(next.name, commitment);
                }
                onPlanSizeChange(next.name);
              }}
            />
          </SliderTrack>
          <MobileSizeBubble
            pct={thumbPct}
            $atEnd={isLastPlan}
            $visible={sliderEngaged}
          >
            <MobileBubbleCaret aria-hidden />
            <MobileBubbleBody>{selectedPlan.label}</MobileBubbleBody>
          </MobileSizeBubble>
          <TickLabels>
            {useMobileLabels
              ? MOBILE_SLIDER_LABEL_NAMES.map((name, i) => {
                  const align =
                    i === 0
                      ? 'left'
                      : i === MOBILE_SLIDER_LABEL_NAMES.length - 1
                        ? 'right'
                        : 'center';
                  return (
                    <Tick key={name} $align={align}>
                      {PLANS[name].label}
                    </Tick>
                  );
                })
              : DIMENSIONAL_PLANS.map((plan, i) => {
                  const show =
                    i === 0 ||
                    i === DIMENSIONAL_PLANS.length - 1 ||
                    i % 2 === 0;
                  return (
                    <Tick key={plan.name} hide={!show}>
                      {plan.label}
                    </Tick>
                  );
                })}
          </TickLabels>
        </SliderWrap>

        {commitment && inclusionTitle && (
          <Banner>
            <BannerTitle>{inclusionTitle}</BannerTitle>
            <InclusionList>
              <FeatureCheck>
                {freeLaborHours} complimentary hours of labor during your
                initial pickup and final delivery.{' '}
                <InlineBrandLink
                  type="button"
                  onClick={() => setShowFinalDeliveryCost(true)}
                >
                  See more details
                </InlineBrandLink>
              </FeatureCheck>
              {freeJobs > 0 && (
                <FeatureCheck>
                  {freeJobs} additional pickup and deliveries during your plan.
                </FeatureCheck>
              )}
              <FeatureCheck>
                Weight-based item protection set above industry standard $1 per
                pound.
              </FeatureCheck>
              <FeatureCheck>Arrival window between 7am – 3pm.</FeatureCheck>
              <FeatureCheck>
                Wrapping and disassembly of your furniture during pickup.
              </FeatureCheck>
              <FeatureCheck>
                Transportation of all boxes and furniture from your home to
                storage facility.
              </FeatureCheck>
            </InclusionList>
          </Banner>
        )}
      </Stack>
      <StorageCalculatorModal
        isOpen={showEstimator}
        selectedItems={storageCalculatorItems}
        onSelectedItemsChange={(items) =>
          onFlowChange(
            'storageCalculatorItems',
            storageCalculatorItemsFromSelectedItems(items),
          )
        }
        onClose={(plan) => {
          setShowEstimator(false);
          if (!plan) return;
          setRecommendedPlan(plan.name);
          onPlanSizeChange(plan.name);
          if (commitment) {
            trackPriceCompleted(plan.name, commitment);
          }
          track({
            action: 'display',
            objectType: 'tooltip',
            objectName: 'recommended_plan_size_tooltip',
            value: plan.name,
          });
        }}
      />
      <FinalDeliveryCostModal
        isOpen={showFinalDeliveryCost}
        onClose={() => setShowFinalDeliveryCost(false)}
      />
    </CardPadded>
  );
};
